import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authorizeAPI, loginAPI } from '../services/authService';
import { ACTIONS } from '../constants/apiEndpoints';

const initialState = {
  statuscode: 0,
  message: null,
  description: null,
  user: null,
  branches: [],
  refreshToken: null,
  selectedBranch: null,
  loading: false,
  error: null,
  authorizedUser: null,
  routes: [],
  isAuthenticated: false
};

export const login = createAsyncThunk(ACTIONS.login, async (credentials, thunkAPI) => {
  try {
    const response = await loginAPI(credentials);

    if (response.data.statuscode !== 200) {
      return thunkAPI.rejectWithValue({
        message: `Login Failed: ${response.data.message}`,
        statuscode: response.data.statuscode,
      });
    }

    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue({
      message: error.response?.data?.message,
      statuscode: error.response?.status || 500
    });
  }
});

export const authorize = createAsyncThunk(
  ACTIONS.authorize,
  async (branchId, thunkAPI) => {
    try {
      const response = await authorizeAPI(branchId);

      if (response.data.statuscode !== 200) {
        return thunkAPI.rejectWithValue({
          message: response.data.message,
          statuscode: response.data.statuscode
        });
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: `Authorization Error: ${error.response?.data?.message}`,
        statuscode: error.response?.status || 500
      });
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      return {
        ...initialState,
        loading: false
      };
    },
    selectBranch: (state, action) => {
      state.selectedBranch = action.payload;
      state.error = null; // Clear any previous errors when selecting branch
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      //Login Cases
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        const { statuscode, message, description, data } = action.payload;
        const { displayName, emailId, contactNo, refreshToken, branches } = data;
        state.statuscode = statuscode;
        state.message = message;
        if((process.env.REACT_APP_ENV === 'local' || process.env.REACT_APP_ENV === 'development') && statuscode === 200){
            sessionStorage.setItem('token', `Bearer ${description}`);
        }
        state.description = description;
        state.loading = false;
        state.user = { displayName, emailId, contactNo };
        state.refreshToken = refreshToken;
        state.branches = branches;
        state.selectedBranch = null;
        state.error = null;
        state.authorizedUser = null;
        state.routes = [];
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Login failed';
        state.isAuthenticated = false;
        // Clear user data on login failure
        state.user = null;
        state.branches = [];
        state.selectedBranch = null;
        state.authorizedUser = null;
        state.routes = [];
      })

      //Authroize Cases
      .addCase(authorize.pending, (state) => {
        state.loading = true;
      })
      .addCase(authorize.fulfilled, (state, action) => {
        state.loading = false;
        const { description, statuscode } = action.payload;
        const { groupName, displayName, societyName, branchName, branchCode, routes } = action.payload.data;
        state.user = null;
        state.branches = [];
        if((process.env.REACT_APP_ENV === 'local' || process.env.REACT_APP_ENV === 'development') && statuscode === 200){
            sessionStorage.setItem('token', `Bearer ${description}`);
        }
        state.selectedBranch = null;
        state.authorizedUser = { groupName, displayName, societyName, branchName, branchCode };
        state.routes = routes;
        state.isAuthenticated = true;
      })
      .addCase(authorize.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload?.message || 'Authorization failed';
      });
  },
});

export const { logout, selectBranch, clearError } = authSlice.actions;
export default authSlice.reducer;
